import { useState, useEffect } from "react";
import { Button, ConfigProvider, InputNumber } from "antd";
import { Minus, Plus } from "lucide-react";
import PropTypes from "prop-types";
import { useCart } from "../../context/UserCartContext.jsx";
import { useTranslation } from "react-i18next";

const AddToCartButton = ({ product }) => {
    const { cart, addToCart, updateCartQty } = useCart();
    const cartItem = cart.find(item => item.id === product.id);
    const [quantity, setQuantity] = useState(cartItem ? cartItem.qty : 1);
    const [isEditing, setIsEditing] = useState(!!cartItem);

    const { t } = useTranslation();

    const handleAddToCart = () => {
        if (product.qty <= 0) return;
        addToCart(product);
        setIsEditing(true);
    };

    useEffect(() => {
        if (cartItem) {
            setQuantity(cartItem.qty);
        }
    }, [cartItem]);

    useEffect(() => {
        if (!cartItem) {
            setIsEditing(false);
            setQuantity(1);
        }
    }, [cartItem]);

    const handleQuantityChange = (value) => {
        const newQty = Math.max(1, Math.min(value || 1, product.qty));
        setQuantity(newQty);
        updateCartQty(product.id, newQty, product.qty);
    };

    return (
        <div className="flex items-center w-full justify-center">
            {isEditing ? (
                <div className="flex w-full items-center justify-center gap-2">
                    <Button
                        type="link"
                        icon={<Minus color="black" size={18} strokeWidth={1} />}
                        onClick={() => handleQuantityChange(quantity - 1)}
                        disabled={quantity <= 1}
                        size="small"
                    />
                    <ConfigProvider
                        theme={{
                            components: {
                                InputNumber: {
                                    controlWidth: "100%"
                                },
                            },
                        }}
                    >
                        <InputNumber
                            type="number"
                            min={1}
                            max={product.qty}
                            value={quantity}
                            onChange={handleQuantityChange}
                            size="large"
                            keyboard={false}
                            style={{
                                borderRadius: 0,
                                textAlignLast: "center",
                            }}
                        />
                    </ConfigProvider>
                    <Button
                        type="link"
                        icon={<Plus color="black" size={18} strokeWidth={1} />}
                        onClick={() => handleQuantityChange(quantity + 1)}
                        disabled={quantity >= product.qty}
                        size="small"
                    />
                </div>
            ) : (
                <Button
                    variant="solid"
                    size="large"
                    className="w-full"
                    style={{
                        backgroundColor: "#0da98c",
                        borderColor: "#0da98c",
                        color: "white",
                        borderRadius: 0,
                        fontSize: "0.775rem"
                    }}
                    onClick={handleAddToCart}
                    disabled={product.qty <= 0}
                >
                    {t("addToCart")}
                </Button>
            )}
        </div>
    );
};

AddToCartButton.propTypes = {
    product: PropTypes.object.isRequired,
};

export default AddToCartButton;
